/**
 * Host-side "Background music" control, shared by the RSVP form builder and
 * the wedding-website builder. Guests hear it through qr-music-player.js.
 *
 *   QRMusicPicker.mount(container, {
 *     value,                       // saved backgroundMusic or null
 *     onChange(value),             // called with the new value (object or null)
 *     upload(file, contentType),   // -> Promise<downloadUrl>; the builder owns the Storage path
 *     ensureSignedIn(retry),       // -> true when signed in; otherwise prompts and returns false
 *     lang                         // optional; defaults to <html lang>
 *   }) -> { setValue(value) }
 *
 * Saved shape (every key always present, never undefined -- Firestore rejects
 * a write containing undefined anywhere):
 *   { enabled: bool, url: string, name: string, source: 'upload'|'library',
 *     trackId: string|null, rightsConfirmedAt: ISO string|null }
 *
 * Switching music off keeps the song (enabled:false), so switching it back on
 * does not mean finding the file again.
 *
 * Uploads must pass a "this is mine to use" confirmation. Hosts will upload
 * commercial songs, and the file lives in our Storage; the confirmation and
 * its timestamp are what our terms rely on if a rights holder ever asks.
 */
(function () {
    'use strict';
    if (window.QRMusicPicker) return;

    var MAX_BYTES = 10 * 1024 * 1024;
    // Formats every browser we serve can play, iPhone included (no Ogg, no
    // WebM). WAV/FLAC are excluded on size: a 10MB WAV is under a minute.
    var TYPES = { mp3: 'audio/mpeg', m4a: 'audio/mp4', aac: 'audio/aac' };

    // Built-in tracks, served from /assets/audio/music/<id>.mp3, produced by
    // scripts/gen-music-library.js with Lyria 3. They are ours, so picking one
    // asks for no rights confirmation and no sign-in. Ids must match the script.
    var LIBRARY = [
        { id: "romantic-piano", names: {"en":"Romantic Piano","es":"Piano romántico","pt":"Piano romântico","fr":"Piano romantique","de":"Romantisches Klavier","he":"פסנתר רומנטי","ar":"بيانو رومانسي"} },
        { id: "string-quartet", names: {"en":"String Quartet","es":"Cuarteto de cuerdas","pt":"Quarteto de cordas","fr":"Quatuor à cordes","de":"Streichquartett","he":"רביעיית מיתרים","ar":"رباعي وتري"} },
        { id: "piano-and-cello", names: {"en":"Piano & Cello","es":"Piano y chelo","pt":"Piano e violoncelo","fr":"Piano et violoncelle","de":"Klavier & Cello","he":"פסנתר וצ׳לו","ar":"بيانو وتشيلو"} },
        { id: "acoustic-guitar", names: {"en":"Acoustic Guitar","es":"Guitarra acústica","pt":"Violão","fr":"Guitare acoustique","de":"Akustikgitarre","he":"גיטרה אקוסטית","ar":"غيتار أكوستيك"} },
        { id: "ambient-folk", names: {"en":"Ambient Folk","es":"Folk ambiental","pt":"Folk ambiente","fr":"Folk planant","de":"Ambient-Folk","he":"פולק אווירתי","ar":"فولك هادئ"} },
        { id: "harp-and-flute", names: {"en":"Harp & Flute","es":"Arpa y flauta","pt":"Harpa e flauta","fr":"Harpe et flûte","de":"Harfe & Flöte","he":"נבל וחליל","ar":"هارب وفلوت"} },
        { id: "elegant-waltz", names: {"en":"Elegant Waltz","es":"Vals elegante","pt":"Valsa elegante","fr":"Valse élégante","de":"Eleganter Walzer","he":"ואלס אלגנטי","ar":"فالس أنيق"} },
        { id: "spanish-guitar", names: {"en":"Spanish Guitar","es":"Guitarra española","pt":"Violão espanhol","fr":"Guitare espagnole","de":"Spanische Gitarre","he":"גיטרה ספרדית","ar":"غيتار إسباني"} },
        { id: "bossa-nova", names: {"en":"Bossa Nova","es":"Bossa nova","pt":"Bossa nova","fr":"Bossa nova","de":"Bossa Nova","he":"בוסה נובה","ar":"بوسا نوفا"} },
        { id: "jazz-lounge", names: {"en":"Jazz Lounge","es":"Jazz lounge","pt":"Jazz lounge","fr":"Jazz lounge","de":"Jazz-Lounge","he":"ג׳אז לאונג׳","ar":"جاز هادئ"} },
        { id: "oud-and-qanun", names: {"en":"Oud & Qanun","es":"Laúd árabe y qanun","pt":"Alaúde e qanun","fr":"Oud et qanûn","de":"Oud & Kanun","he":"עוד וקאנון","ar":"عود وقانون"} },
        { id: "whimsical-woodwinds", names: {"en":"Whimsical Woodwinds","es":"Vientos juguetones","pt":"Sopros divertidos","fr":"Bois espiègles","de":"Verspielte Holzbläser","he":"כלי נשיפה שובבים","ar":"آلات نفخ مرحة"} },
        { id: "sitar-and-bansuri", names: {"en":"Sitar & Bansuri","es":"Sitar y bansuri","pt":"Cítara e bansuri","fr":"Sitar et bansuri","de":"Sitar & Bansuri","he":"סיטאר ובנסורי","ar":"سيتار وبانسوري"} },
        { id: "cinematic-strings", names: {"en":"Cinematic Strings","es":"Cuerdas cinematográficas","pt":"Cordas cinematográficas","fr":"Cordes cinématographiques","de":"Filmische Streicher","he":"מיתרים קולנועיים","ar":"أوتار سينمائية"} },
        { id: "festive-celebration", names: {"en":"Festive Celebration","es":"Fiesta alegre","pt":"Festa alegre","fr":"Fête joyeuse","de":"Fröhliches Fest","he":"חגיגה שמחה","ar":"احتفال مبهج"} },
        { id: "playful-kids", names: {"en":"Playful Kids Party","es":"Fiesta infantil","pt":"Festa infantil","fr":"Fête d'enfants","de":"Kinderparty","he":"מסיבת ילדים","ar":"حفلة أطفال"} },
        { id: "rock-anthem", names: {"en":"Rock Anthem","es":"Himno rockero","pt":"Hino do rock","fr":"Hymne rock","de":"Rock-Hymne","he":"המנון רוק","ar":"نشيد روك"} },
        { id: "heavy-metal", names: {"en":"Heavy Metal","es":"Heavy metal","pt":"Heavy metal","fr":"Heavy metal","de":"Heavy Metal","he":"הבי מטאל","ar":"هيفي ميتال"} },
        { id: "pop-punk", names: {"en":"Pop Punk","es":"Pop punk","pt":"Pop punk","fr":"Pop punk","de":"Pop-Punk","he":"פופ פאנק","ar":"بوب بانك"} },
        { id: "hip-hop-beat", names: {"en":"Hip Hop Beat","es":"Ritmo hip hop","pt":"Batida hip hop","fr":"Beat hip-hop","de":"Hip-Hop-Beat","he":"ביט היפ הופ","ar":"إيقاع هيب هوب"} },
        { id: "trap-beat", names: {"en":"Trap Beat","es":"Ritmo trap","pt":"Batida trap","fr":"Beat trap","de":"Trap-Beat","he":"ביט טראפ","ar":"إيقاع تراب"} },
        { id: "electro-house", names: {"en":"Electro House","es":"Electro house","pt":"Electro house","fr":"Électro house","de":"Electro House","he":"אלקטרו האוס","ar":"إلكترو هاوس"} },
        { id: "funk-disco", names: {"en":"Funk & Disco","es":"Funk y disco","pt":"Funk e disco","fr":"Funk et disco","de":"Funk & Disco","he":"פאנק ודיסקו","ar":"فانك وديسكو"} },
        { id: "cumbia-party", names: {"en":"Cumbia Party","es":"Fiesta de cumbia","pt":"Festa de cúmbia","fr":"Fête cumbia","de":"Cumbia-Party","he":"מסיבת קומביה","ar":"حفلة كومبيا"} }
    ];

    var UI = {
        en: {
            toggle: 'Play background music', create: 'Create your own song with custom lyrics', createSub: 'Your names and your story, sung in the style you pick. Free preview, $12 to keep.', editSong: 'Edit song', mySong: 'Your personalized song', envelopeTip: "Tip: for music from the very first moment, turn on the envelope. The tap that opens it starts the song.",
            hint: 'The music starts when a guest opens the envelope or first taps the page. A button in the corner lets them pause it at any time.',
            upload: 'Upload a song', replace: 'Replace', remove: 'Remove',
            formats: 'MP3 or M4A, up to 10MB.',
            rights: 'I own this music or have permission to use it.',
            rightsNeeded: 'Please confirm you have the right to use this music first.',
            library: 'Choose one of our songs', own: 'Or upload your own song', yours: 'Your song',
            listen: 'Listen', stop: 'Stop',
            uploading: 'Uploading your song...',
            tooBig: 'That file is over 10MB. Please use a shorter or more compressed version.',
            badType: 'Please choose an MP3 or M4A file.',
            unplayable: 'This file could not be played. Please try an MP3.',
            failed: 'The upload did not finish. Please try again.'
        },
        es: {
            toggle: 'Poner música de fondo', create: 'Crea tu propia canción con letra personalizada', createSub: 'Sus nombres y su historia, cantados en el estilo que elijas. Vista previa gratis, 12 US$ para quedártela.', editSong: 'Editar canción', mySong: 'Tu canción personalizada', envelopeTip: "Consejo: para que la música suene desde el primer momento, activa el sobre. El toque que lo abre inicia la canción.",
            hint: 'La música empieza cuando el invitado abre el sobre o toca la página por primera vez. Un botón en la esquina le permite pausarla cuando quiera.',
            upload: 'Subir una canción', replace: 'Cambiar', remove: 'Quitar',
            formats: 'MP3 o M4A, hasta 10 MB.',
            rights: 'Esta música es mía o tengo permiso para usarla.',
            rightsNeeded: 'Primero confirma que tienes derecho a usar esta música.',
            library: 'Elige una de nuestras canciones', own: 'O sube tu propia canción', yours: 'Tu canción',
            listen: 'Escuchar', stop: 'Detener',
            uploading: 'Subiendo tu canción...',
            tooBig: 'El archivo pesa más de 10 MB. Usa una versión más corta o más comprimida.',
            badType: 'Elige un archivo MP3 o M4A.',
            unplayable: 'No se pudo reproducir este archivo. Prueba con un MP3.',
            failed: 'La subida no terminó. Inténtalo de nuevo.'
        },
        pt: {
            toggle: 'Tocar música de fundo', create: 'Crie sua própria música com letra personalizada', createSub: 'Seus nomes e sua história, cantados no estilo que você escolher. Prévia grátis, US$ 12 para ficar com ela.', editSong: 'Editar música', mySong: 'Sua música personalizada', envelopeTip: "Dica: para a música tocar desde o primeiro momento, ative o envelope. O toque que o abre inicia a música.",
            hint: 'A música começa quando o convidado abre o envelope ou toca na página pela primeira vez. Um botão no canto permite pausá-la quando quiser.',
            upload: 'Enviar uma música', replace: 'Trocar', remove: 'Remover',
            formats: 'MP3 ou M4A, até 10 MB.',
            rights: 'Esta música é minha ou tenho permissão para usá-la.',
            rightsNeeded: 'Primeiro confirme que você tem o direito de usar esta música.',
            library: 'Escolha uma das nossas músicas', own: 'Ou envie sua própria música', yours: 'Sua música',
            listen: 'Ouvir', stop: 'Parar',
            uploading: 'Enviando sua música...',
            tooBig: 'O arquivo tem mais de 10 MB. Use uma versão mais curta ou mais compactada.',
            badType: 'Escolha um arquivo MP3 ou M4A.',
            unplayable: 'Não foi possível tocar este arquivo. Tente um MP3.',
            failed: 'O envio não terminou. Tente novamente.'
        },
        fr: {
            toggle: 'Ajouter une musique de fond', create: 'Créez votre chanson avec des paroles personnalisées', createSub: 'Vos prénoms et votre histoire, chantés dans le style choisi. Aperçu gratuit, 12 $ US pour la garder.', editSong: 'Modifier la chanson', mySong: 'Votre chanson personnalisée', envelopeTip: "Astuce : pour que la musique démarre dès le premier instant, activez l'enveloppe. Le geste qui l'ouvre lance la chanson.",
            hint: "La musique démarre quand l'invité ouvre l'enveloppe ou touche la page pour la première fois. Un bouton dans le coin lui permet de la mettre en pause à tout moment.",
            upload: 'Importer une chanson', replace: 'Remplacer', remove: 'Retirer',
            formats: "MP3 ou M4A, jusqu'à 10 Mo.",
            rights: "Cette musique m'appartient ou j'ai l'autorisation de l'utiliser.",
            rightsNeeded: "Confirmez d'abord que vous avez le droit d'utiliser cette musique.",
            library: 'Choisissez une de nos musiques', own: 'Ou importez votre propre chanson', yours: 'Votre chanson',
            listen: 'Écouter', stop: 'Arrêter',
            uploading: 'Envoi de votre chanson...',
            tooBig: 'Ce fichier dépasse 10 Mo. Utilisez une version plus courte ou plus compressée.',
            badType: 'Choisissez un fichier MP3 ou M4A.',
            unplayable: "Ce fichier n'a pas pu être lu. Essayez un MP3.",
            failed: "L'envoi n'a pas abouti. Réessayez."
        },
        de: {
            toggle: 'Hintergrundmusik abspielen', create: 'Ihr eigener Song mit persönlichem Text', createSub: 'Ihre Namen und Ihre Geschichte, gesungen im gewählten Stil. Kostenlose Hörprobe, 12 US$ zum Behalten.', editSong: 'Song bearbeiten', mySong: 'Ihr persönlicher Song', envelopeTip: "Tipp: Damit die Musik vom ersten Moment an spielt, schalten Sie den Umschlag ein. Das Tippen zum Öffnen startet den Song.",
            hint: 'Die Musik startet, wenn ein Gast den Umschlag öffnet oder zum ersten Mal auf die Seite tippt. Mit einer Taste in der Ecke kann er sie jederzeit pausieren.',
            upload: 'Song hochladen', replace: 'Ersetzen', remove: 'Entfernen',
            formats: 'MP3 oder M4A, bis 10 MB.',
            rights: 'Die Musik gehört mir oder ich darf sie verwenden.',
            rightsNeeded: 'Bitte bestätigen Sie zuerst, dass Sie diese Musik verwenden dürfen.',
            library: 'Wählen Sie einen unserer Titel', own: 'Oder laden Sie Ihren eigenen Song hoch', yours: 'Ihr Song',
            listen: 'Anhören', stop: 'Stopp',
            uploading: 'Ihr Song wird hochgeladen...',
            tooBig: 'Die Datei ist größer als 10 MB. Bitte verwenden Sie eine kürzere oder stärker komprimierte Version.',
            badType: 'Bitte wählen Sie eine MP3- oder M4A-Datei.',
            unplayable: 'Diese Datei konnte nicht abgespielt werden. Versuchen Sie es mit einer MP3.',
            failed: 'Der Upload wurde nicht abgeschlossen. Bitte versuchen Sie es erneut.'
        },
        he: {
            toggle: 'השמעת מוזיקת רקע', create: 'צרו שיר משלכם עם מילים אישיות', createSub: 'השמות והסיפור שלכם, מושרים בסגנון שתבחרו. טעימה בחינם, 12$ לשיר המלא.', editSong: 'עריכת השיר', mySong: 'השיר האישי שלכם', envelopeTip: "טיפ: כדי שהמוזיקה תתחיל מהרגע הראשון, הפעילו את המעטפה. הנגיעה שפותחת אותה מתחילה את השיר.",
            hint: 'המוזיקה מתחילה כשהאורח פותח את המעטפה או נוגע בדף בפעם הראשונה. כפתור בפינה מאפשר לו להשהות אותה בכל רגע.',
            upload: 'העלאת שיר', replace: 'החלפה', remove: 'הסרה',
            formats: 'MP3 או M4A, עד 10MB.',
            rights: 'המוזיקה הזו שלי או שיש לי רשות להשתמש בה.',
            rightsNeeded: 'יש לאשר קודם שיש לכם זכות להשתמש במוזיקה הזו.',
            library: 'בחרו אחד מהשירים שלנו', own: 'או העלו שיר משלכם', yours: 'השיר שלכם',
            listen: 'האזנה', stop: 'עצירה',
            uploading: 'מעלה את השיר...',
            tooBig: 'הקובץ גדול מ-10MB. נסו גרסה קצרה או דחוסה יותר.',
            badType: 'יש לבחור קובץ MP3 או M4A.',
            unplayable: 'לא ניתן היה להשמיע את הקובץ. נסו קובץ MP3.',
            failed: 'ההעלאה לא הושלמה. נסו שוב.'
        },
        ar: {
            toggle: 'تشغيل موسيقى في الخلفية', create: 'أنشئ أغنيتك بكلمات خاصة بك', createSub: 'أسماؤكم وقصتكم، مغنّاة بالأسلوب الذي تختاره. معاينة مجانية، و12 دولارًا للاحتفاظ بها.', editSong: 'تعديل الأغنية', mySong: 'أغنيتك الخاصة', envelopeTip: "نصيحة: لتبدأ الموسيقى من اللحظة الأولى، فعّل الظرف. اللمسة التي تفتحه تبدأ الأغنية.",
            hint: 'تبدأ الموسيقى عندما يفتح الضيف الظرف أو يلمس الصفحة أول مرة. ويتيح له زر في الزاوية إيقافها مؤقتًا في أي وقت.',
            upload: 'رفع أغنية', replace: 'استبدال', remove: 'إزالة',
            formats: 'MP3 أو M4A، حتى 10 ميغابايت.',
            rights: 'هذه الموسيقى ملكي أو لدي إذن باستخدامها.',
            rightsNeeded: 'يُرجى التأكيد أولًا أن لديك الحق في استخدام هذه الموسيقى.',
            library: 'اختر إحدى مقطوعاتنا', own: 'أو ارفع أغنيتك الخاصة', yours: 'أغنيتك',
            listen: 'استماع', stop: 'إيقاف',
            uploading: 'جارٍ رفع أغنيتك...',
            tooBig: 'حجم الملف أكبر من 10 ميغابايت. يُرجى استخدام نسخة أقصر أو مضغوطة أكثر.',
            badType: 'يُرجى اختيار ملف MP3 أو M4A.',
            unplayable: 'تعذّر تشغيل هذا الملف. جرّب ملف MP3.',
            failed: 'لم يكتمل الرفع. يُرجى المحاولة مرة أخرى.'
        }
    };

    function esc(s) {
        return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
    }

    function extOf(name) {
        var m = /\.([a-z0-9]+)$/i.exec(name || '');
        return m ? m[1].toLowerCase() : '';
    }

    /** Display name from a file name: no extension, no path noise, bounded. */
    function niceName(fileName) {
        return String(fileName || '').replace(/\.[a-z0-9]+$/i, '').replace(/[_]+/g, ' ').trim().slice(0, 80) || 'Song';
    }

    /** Resolve true when this browser can decode the file, false otherwise. */
    function canPlay(file) {
        return new Promise(function (resolve) {
            var url = URL.createObjectURL(file);
            var a = new Audio();
            var settled = false;
            function finish(ok) {
                if (settled) return;
                settled = true;
                URL.revokeObjectURL(url);
                resolve(ok);
            }
            a.preload = 'metadata';
            a.addEventListener('loadedmetadata', function () { finish(isFinite(a.duration) ? a.duration > 0 : true); });
            a.addEventListener('error', function () { finish(false); });
            // Some engines never fire either for a valid file without a user
            // gesture; do not hold the host hostage to that.
            setTimeout(function () { finish(true); }, 4000);
            a.src = url;
        });
    }

    /** Normalize anything saved earlier into the full shape, or null. */
    function normalize(v) {
        if (!v || typeof v !== 'object' || typeof v.url !== 'string' || !v.url) return null;
        return {
            enabled: v.enabled === true,
            url: v.url,
            name: typeof v.name === 'string' ? v.name.slice(0, 80) : '',
            source: ['library', 'generated'].includes(v.source) ? v.source : 'upload', // generated = song generator
            trackId: typeof v.trackId === 'string' ? v.trackId : null,
            rightsConfirmedAt: typeof v.rightsConfirmedAt === 'string' ? v.rightsConfirmedAt : null
        };
    }

    var uid = 0;

    function mount(container, opts) {
        if (!container) return null;
        opts = opts || {};
        var id = 'qrmp' + (++uid);
        var lang = (opts.lang || document.documentElement.lang || 'en').toLowerCase().split('-')[0];
        var T = UI[lang] || UI.en;
        var value = normalize(opts.value);
        // "On" with nothing chosen yet: the switch shows on, guests hear nothing
        // until a song is picked. Not saved until there is a song.
        var wantOn = !!(value && value.enabled);
        var busy = false;
        var error = '';
        var rightsChecked = false;
        var audition = null;
        var auditionUrl = null;

        function emit(next) {
            value = next;
            if (typeof opts.onChange === 'function') opts.onChange(value ? normalize(value) : null);
        }

        function stopAudition() {
            if (audition) { try { audition.pause(); } catch (e) { /* ignore */ } }
            auditionUrl = null;
        }

        function toggleAudition(url) {
            if (auditionUrl === url) { stopAudition(); render(); return; }
            stopAudition();
            if (!audition) {
                audition = new Audio();
                audition.addEventListener('ended', function () { auditionUrl = null; render(); });
            }
            audition.src = url;
            auditionUrl = url;
            var p = audition.play();
            if (p && p.catch) p.catch(function () { auditionUrl = null; render(); });
            render();
        }

        function trackName(t) { return t.names[lang] || t.names.en; }

        function libraryHtml() {
            if (!LIBRARY.length) return '';
            var rows = LIBRARY.map(function (t) {
                var url = '/assets/audio/music/' + t.id + '.mp3';
                var active = value && value.source === 'library' && value.trackId === t.id;
                var listening = auditionUrl === url;
                return '<div class="qrmp-track' + (active ? ' is-active' : '') + '">'
                    + '<button type="button" class="qrmp-pick" data-pick="' + esc(t.id) + '" aria-pressed="' + (active ? 'true' : 'false') + '">'
                    + '<i class="bi ' + (active ? 'bi-check-circle-fill' : 'bi-circle') + '" aria-hidden="true"></i> ' + esc(trackName(t)) + '</button>'
                    + '<button type="button" class="btn btn-link btn-sm qrmp-listen" data-listen="' + esc(url) + '">'
                    + '<i class="bi ' + (listening ? 'bi-stop-fill' : 'bi-play-fill') + '" aria-hidden="true"></i> ' + esc(listening ? T.stop : T.listen) + '</button>'
                    + '</div>';
            }).join('');
            return '<div class="qrmp-label">' + esc(T.library) + '</div><div class="qrmp-library">' + rows + '</div>';
        }

        function render() {
            var on = wantOn;
            var hasUpload = value && value.source === 'upload';
            var hasSong = value && value.source === 'generated';
            var listening = value && auditionUrl === value.url;
            var html = ''
                + '<div class="form-check form-switch mb-2">'
                + '<input class="form-check-input" type="checkbox" id="' + id + '-on"' + (on ? ' checked' : '') + '>'
                + '<label class="form-check-label fw-semibold" for="' + id + '-on">' + esc(T.toggle) + '</label>'
                + '</div>'
                + '<div class="text-muted small mb-3">' + esc(T.hint) + '</div>';
            // Music can only start on a tap; the envelope is what turns that tap
            // into "the song begins as the invitation opens". Shown quietly, only
            // while music is on and the host's builder says the envelope is off.
            if (on && typeof opts.envelopeTip === 'function' && opts.envelopeTip()) {
                html += '<div class="qrmp-tip small mb-3"><i class="bi bi-lightbulb" aria-hidden="true"></i> ' + esc(T.envelopeTip) + '</div>';
            }
            if (on) {
                if (hasSong) {
                    html += '<div class="qrmp-label">' + esc(T.mySong) + '</div>'
                        + '<div class="qrmp-current">'
                        + '<i class="bi bi-stars" aria-hidden="true"></i>'
                        + '<span class="qrmp-name" dir="auto">' + esc(value.name || T.mySong) + '</span>'
                        + '<button type="button" class="btn btn-link btn-sm" data-listen="' + esc(value.url) + '">'
                        + '<i class="bi ' + (listening ? 'bi-stop-fill' : 'bi-play-fill') + '" aria-hidden="true"></i> ' + esc(listening ? T.stop : T.listen) + '</button>'
                        + '<button type="button" class="btn btn-link btn-sm" data-act="song">' + esc(T.editSong) + '</button>'
                        + '<button type="button" class="btn btn-link btn-sm text-danger" data-act="remove">' + esc(T.remove) + '</button>'
                        + '</div>';
                }
                if (hasUpload) {
                    html += '<div class="qrmp-label">' + esc(T.yours) + '</div>'
                        + '<div class="qrmp-current">'
                        + '<i class="bi bi-music-note-beamed" aria-hidden="true"></i>'
                        + '<span class="qrmp-name" dir="auto">' + esc(value.name || T.yours) + '</span>'
                        + '<button type="button" class="btn btn-link btn-sm" data-listen="' + esc(value.url) + '">'
                        + '<i class="bi ' + (listening ? 'bi-stop-fill' : 'bi-play-fill') + '" aria-hidden="true"></i> ' + esc(listening ? T.stop : T.listen) + '</button>'
                        + '<button type="button" class="btn btn-link btn-sm" data-act="replace">' + esc(T.replace) + '</button>'
                        + '<button type="button" class="btn btn-link btn-sm text-danger" data-act="remove">' + esc(T.remove) + '</button>'
                        + '</div>';
                }
                if (typeof opts.songCreator === 'function' && !hasSong) {
                    html += '<button type="button" class="qrmp-create" data-act="song">'
                        + '<span class="qrmp-create-icon" aria-hidden="true">✨</span>'
                        + '<span><b>' + esc(T.create) + '</b><small>' + esc(T.createSub) + '</small></span></button>';
                }
                html += libraryHtml();
                if (LIBRARY.length) html += '<div class="qrmp-label mt-3">' + esc(T.own) + '</div>';
                html += '<div class="form-check mt-2 mb-2">'
                    + '<input class="form-check-input" type="checkbox" id="' + id + '-rights"' + (rightsChecked ? ' checked' : '') + '>'
                    + '<label class="form-check-label small" for="' + id + '-rights">' + esc(T.rights) + '</label>'
                    + '</div>';
                if (!hasUpload) {
                    html += '<button type="button" class="btn btn-outline-primary btn-sm" data-act="upload"' + (busy ? ' disabled' : '') + '>'
                        + '<i class="bi bi-upload me-1" aria-hidden="true"></i>' + esc(busy ? T.uploading : T.upload) + '</button>';
                }
                html += '<div class="text-muted small mt-1">' + esc(T.formats) + '</div>';
                if (error) html += '<div class="text-danger small mt-2" role="alert">' + esc(error) + '</div>';
                html += '<input type="file" class="d-none" id="' + id + '-file" accept=".mp3,.m4a,.aac,audio/mpeg,audio/mp4,audio/aac,audio/x-m4a">';
            }
            // Every change redraws the picker, which reset the song list to its
            // top and dropped focus: picking a song further down made the list
            // jump. Carry the list's scroll and the focused control across.
            var oldList = container.querySelector('.qrmp-library');
            var listTop = oldList ? oldList.scrollTop : 0;
            var focused = document.activeElement && container.contains(document.activeElement) ? document.activeElement : null;
            var focusKey = focused ? ['data-pick', 'data-listen', 'data-act', 'id'].map(function (k) { return focused.getAttribute(k) ? '[' + k + '="' + focused.getAttribute(k) + '"]' : ''; }).join('') : '';
            container.innerHTML = '<div class="qrmp">' + html + '</div>';
            var newList = container.querySelector('.qrmp-library');
            if (newList && listTop) newList.scrollTop = listTop;
            if (focusKey) {
                var again = null;
                try { again = container.querySelector(focusKey); } catch (e) { /* odd id */ }
                if (again) { try { again.focus({ preventScroll: true }); } catch (e) { again.focus(); } }
            }
            // Lets the host page reflect the state (the form builder's feature tile).
            try { container.dispatchEvent(new CustomEvent('qrmusic:render', { bubbles: true })); } catch (e) { /* old engine */ }
        }

        /** The paid song generator (song-creator.js); signed-in hosts only. */
        function openSongCreator(projectId, confirmPayment) {
            if (!window.QRSongCreator || typeof opts.songCreator !== 'function') return;
            if (typeof opts.ensureSignedIn === 'function' && !opts.ensureSignedIn(function () { openSongCreator(projectId, confirmPayment); })) return;
            stopAudition();
            window.QRSongCreator.open({
                context: opts.songCreator() || {},
                lang: lang,
                projectId: projectId || (value && value.source === 'generated' ? value.trackId : null) || null,
                currentUrl: value ? value.url : '',
                confirmPayment: !!confirmPayment,
                onUse: function (v) { wantOn = true; emit(v); render(); }
            });
        }

        function chooseFile() {
            if (!rightsChecked) {
                error = T.rightsNeeded;
                render();
                var r = document.getElementById(id + '-rights');
                if (r) r.focus();
                return;
            }
            var input = document.getElementById(id + '-file');
            if (input) input.click();
        }

        function handleFile(file) {
            if (!file) return;
            var ext = extOf(file.name);
            var type = TYPES[ext];
            error = '';
            if (!type) { error = T.badType; render(); return; }
            if (file.size > MAX_BYTES) { error = T.tooBig; render(); return; }
            // Sign-in is checked once the file is chosen, so the builder can replay
            // the upload with this same file after the host signs in. (Opening
            // the file picker again from that callback would be blocked: it is
            // no longer inside the host's tap.)
            if (typeof opts.ensureSignedIn === 'function' && !opts.ensureSignedIn(function () { handleFile(file); })) return;
            busy = true;
            render();
            canPlay(file).then(function (ok) {
                if (!ok) throw new Error('UNPLAYABLE');
                return opts.upload(file, type);
            }).then(function (url) {
                busy = false;
                rightsChecked = false;
                stopAudition();
                emit({
                    enabled: true,
                    url: url,
                    name: niceName(file.name),
                    source: 'upload',
                    trackId: null,
                    rightsConfirmedAt: new Date().toISOString()
                });
                render();
            }).catch(function (err) {
                busy = false;
                console.error('[music-picker] upload failed', err);
                error = err && err.message === 'UNPLAYABLE' ? T.unplayable : T.failed;
                render();
            });
        }

        container.addEventListener('change', function (e) {
            var t = e.target;
            if (t.id === id + '-on') {
                wantOn = t.checked;
                error = '';
                if (!wantOn) stopAudition();
                if (value) emit(Object.assign({}, value, { enabled: wantOn }));
                render();
            } else if (t.id === id + '-file') {
                handleFile(t.files && t.files[0]);
                t.value = '';
            } else if (t.id === id + '-rights') {
                rightsChecked = t.checked;
                if (rightsChecked && error === T.rightsNeeded) { error = ''; render(); }
            }
        });

        container.addEventListener('click', function (e) {
            var b = e.target.closest('button');
            if (!b || !container.contains(b)) return;
            if (b.hasAttribute('data-listen')) { toggleAudition(b.getAttribute('data-listen')); return; }
            if (b.hasAttribute('data-pick')) {
                var tid = b.getAttribute('data-pick');
                var t = LIBRARY.find(function (x) { return x.id === tid; });
                if (!t) return;
                var pickUrl = '/assets/audio/music/' + t.id + '.mp3';
                error = '';
                emit({ enabled: true, url: pickUrl, name: trackName(t), source: 'library', trackId: t.id, rightsConfirmedAt: null });
                // Picking a song plays it (task #1063), switching off whatever
                // was playing; hearing it is how a host knows it is the one.
                // Picking the song already playing leaves it playing.
                if (auditionUrl !== pickUrl) toggleAudition(pickUrl);
                else render();
                return;
            }
            var act = b.getAttribute('data-act');
            if (act === 'song') { openSongCreator(); return; }
            if (act === 'upload' || act === 'replace') chooseFile();
            else if (act === 'remove') {
                stopAudition();
                emit(null);
                render();
            }
        });

        ensureStyles();
        render();

        return {
            setValue: function (v) {
                value = normalize(v);
                wantOn = !!(value && value.enabled);
                stopAudition();
                error = '';
                render();
            },
            getValue: function () { return value ? normalize(value) : null; },
            /** Redraw without touching the value (e.g. the envelope was switched). */
            refresh: function () { render(); },
            /** Reopen the song generator on a project (the Stripe return). */
            openSong: function (projectId, confirmPayment) { openSongCreator(projectId, confirmPayment); }
        };
    }

    function ensureStyles() {
        if (document.getElementById('qrmp-style')) return;
        var s = document.createElement('style');
        s.id = 'qrmp-style';
        s.textContent = ''
            + '.qrmp-label{font-size:.8rem;font-weight:600;color:inherit;opacity:.75;margin-bottom:.35rem}'
            + '.qrmp-current{display:flex;flex-wrap:wrap;align-items:center;gap:.25rem .5rem;padding:.5rem .75rem;'
            + 'border:1px solid #dee2e6;border-radius:10px;background:#fafafa;color:#2b2622;margin-bottom:.5rem}'
            + '.qrmp-current .btn-link{padding:.15rem .25rem;text-decoration:none}'
            + '.qrmp-name{flex:1 1 140px;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:500}'
            + '.qrmp-library{display:flex;flex-direction:column;gap:.15rem;max-height:260px;overflow-y:auto;border:1px solid #eee;border-radius:10px;padding:.25rem}'
            + '.qrmp-track{display:flex;align-items:center;justify-content:space-between;gap:.5rem;padding:.25rem .5rem;border-radius:8px}'
            + '.qrmp-track.is-active{background:#eef4ff;color:#1f2a44}'
            + '.qrmp-pick{border:0;background:none;color:inherit;padding:.25rem 0;text-align:start;flex:1;cursor:pointer}'
            + '.qrmp-listen{text-decoration:none;white-space:nowrap}'
            + '.qrmp-create{display:flex;align-items:center;gap:.7rem;width:100%;text-align:start;border:1.5px solid #e2c98f;border-radius:14px;padding:.75rem .9rem;margin:0 0 .9rem;cursor:pointer;background:linear-gradient(135deg,#fdf8ee,#f8eedb)}'
            + '.qrmp-create:hover{border-color:#c9a24a}'
            + '.qrmp-create-icon{font-size:1.4rem}'
            + '.qrmp-create b{display:block;font-size:.95rem;color:#5b4520}'
            + '.qrmp-create small{display:block;color:#7a6a52;font-size:.8rem;line-height:1.35;margin-top:2px}'
            + '.qrmp-tip{color:#5f584e;background:#f8f6f1;border-radius:8px;padding:.4rem .6rem}';
        (document.head || document.documentElement).appendChild(s);
    }

    window.QRMusicPicker = { mount: mount, normalize: normalize, LIBRARY: LIBRARY };
})();
